import {useEffect, useState} from 'react';

import {formatDate, formatTime, initials} from '../format';
import {Grid} from './Icons';
import {useStudio} from '../store';
import {THEME_GLYPH, THEME_LABEL, useViewPrefs} from '../theme';
import type {Screen} from '../types';

/**
 * The product chooser. This is the one absolute URL in `src/` and it does not
 * break the root-relative rule: that rule exists so same-origin assets and API
 * calls never hardcode a host, and this is a different site on a different
 * origin, which cannot be expressed any other way.
 */
const PORTAL_URL = 'https://podstudio.liminal.ps/';

const SCREENS: {key: Screen; label: string}[] = [
  {key: 'brief', label: 'موجز'},
  {key: 'workspace', label: 'مساحة العمل'},
  {key: 'preview', label: 'المعاينة'},
];

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 20_000);
    return () => clearInterval(timer);
  }, []);
  return (
    <>
      <span>{formatDate(now.toISOString())}</span>
      <span className="util-sep" />
      <span>{formatTime(now)}</span>
    </>
  );
}

export function Chrome() {
  const s = useStudio();
  const {theme, motion, cycleTheme, toggleMotion} = useViewPrefs();
  const hasProject = !!s.project;

  return (
    <>
      <div className="util">
        <div className="shell">
          <div className="util-left">
            <a className="util-home" href={PORTAL_URL} title="العودة إلى منظومة الإنتاج الذكي">
              <Grid size={11} />
              <span>المنظومة</span>
            </a>
            <span className="util-sep" />
            <span className="util-tag">عرض توضيحي</span>
            {SCREENS.map(({key, label}) => (
              <button
                key={key}
                type="button"
                className={`pill${s.screen === key ? ' on' : ''}`}
                disabled={key !== 'brief' && !hasProject}
                onClick={() => s.go(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="wordmark">دبي للإعلام</div>
          <div className="util-right">
            <Clock />
            <span className="util-sep" />
            <button
              type="button"
              className="util-btn"
              title={`الوضع البصري: ${THEME_LABEL[theme]}`}
              aria-label={`الوضع البصري: ${THEME_LABEL[theme]}`}
              onClick={cycleTheme}
            >
              {THEME_GLYPH[theme]}
            </button>
            <button
              type="button"
              className={`util-btn${motion === 'still' ? ' on' : ''}`}
              title={motion === 'still' ? 'الحركة موقوفة' : 'إيقاف الحركة'}
              aria-label={motion === 'still' ? 'الحركة موقوفة' : 'إيقاف الحركة'}
              aria-pressed={motion === 'still'}
              onClick={toggleMotion}
            >
              ❋
            </button>
          </div>
        </div>
      </div>

      <div className="nav">
        <div className="shell">
          <div className="brand">
            {/* The colour lockup carries slate lettering that disappears on the dark
                surface, so the theme picks the variant rather than a CSS filter —
                a hue-rotate on a trademark is exactly the kind of "close enough"
                that shows up on a projector. */}
            <img
              className="brand-logo"
              src={theme === 'dark' ? '/brand/dmi-logo-white.png' : '/brand/dmi-logo.png'}
              alt="دبي للإعلام"
              width={243}
              height={214}
            />
            <span className="brand-text">
              <b>استوديو الفيديو</b>
              <span>AI Video Studio</span>
            </span>
          </div>
          <nav>
            <a className={s.screen === 'brief' ? 'on' : ''} onClick={() => s.newBrief()}>
              موجز جديد
            </a>
            <a
              onClick={() => {
                s.go('brief');
                requestAnimationFrame(() =>
                  document.getElementById('recent')?.scrollIntoView({behavior: 'smooth'}),
                );
              }}
            >
              مشاريعي
            </a>
            <a onClick={() => void s.openArchive()}>الأرشيف</a>
          </nav>
          <button type="button" className="user-chip" title="تغيير المستخدم" onClick={s.openSignIn}>
            <span>{s.user ? s.user.display_name : 'تعريف'}</span>
            <span className="avatar">{s.user ? initials(s.user.display_name) : '؟'}</span>
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * The one place the builder is named. Everything above this line is the client's
 * brand, so the credit lives here and nowhere else.
 */
export function SiteFooter() {
  return (
    <footer className="site-foot">
      <div className="shell">
        {/* Repeated from the utility bar: the workspace is long enough that the
            top strip is well out of view by the time anyone wants to leave. */}
        <a href={PORTAL_URL}>
          <Grid size={11} />
          <span>العودة إلى المنظومة</span>
        </a>
        <span className="site-foot-sep" />
        <span>تم تطويره في ليمنال للحلول الذكية والتقنية</span>
      </div>
    </footer>
  );
}
